import imageCompression from 'browser-image-compression';
import Exifr from 'exifr';

// EXIF orientation → degrees to rotate clockwise
const EXIF_DEGREES: Record<number, number> = { 3: 180, 6: 90, 8: -90 };

async function readOrientation(file: File): Promise<number> {
  try {
    const data = await Exifr.parse(file, { pick: ['Orientation'] });
    return (data?.Orientation as number | undefined) ?? 1;
  } catch {
    return 1;
  }
}

function rotateBlob(blob: Blob, degrees: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img  = new Image();

    img.onload = () => {
      const { naturalWidth: w, naturalHeight: h } = img;
      const swap   = Math.abs(degrees) === 90;
      const canvas = document.createElement('canvas');
      canvas.width  = swap ? h : w;
      canvas.height = swap ? w : h;

      const ctx = canvas.getContext('2d')!;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.drawImage(img, -w / 2, -h / 2);

      URL.revokeObjectURL(url);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob falló'))),
        'image/webp',
        0.82,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo decodificar la imagen'));
    };

    img.src = url;
  });
}

/**
 * Compresses, resizes to ≤1920 px, corrects EXIF rotation and converts to WebP.
 * Replaces the basic canvas-only conversion from Phase 4.
 */
export async function processImageForUpload(file: File): Promise<File> {
  // Read orientation BEFORE compression strips EXIF data
  const orientation = await readOrientation(file);

  // Compress + resize + convert to WebP
  const compressed = await imageCompression(file, {
    maxSizeMB:        0.85,
    maxWidthOrHeight: 1920,
    useWebWorker:     true,
    fileType:         'image/webp',
    initialQuality:   0.82,
  });

  // Apply EXIF rotation if needed
  const degrees    = EXIF_DEGREES[orientation] ?? 0;
  const finalBlob  = degrees !== 0 ? await rotateBlob(compressed, degrees) : compressed;

  return new File(
    [finalBlob],
    file.name.replace(/\.[^.]+$/, '.webp'),
    { type: 'image/webp' },
  );
}
